import {
  ConfigPlugin,
  withGradleProperties,
  withProjectBuildGradle,
  withMainApplication,
  withSettingsGradle,
  withAppBuildGradle,
  AndroidConfig,
} from '@expo/config-plugins';
import { mergeContents } from '@expo/config-plugins/build/utils/generateCode';
import { addImports } from '@expo/config-plugins/build/android/codeMod';

const DEFAULT_SDK_VERSION = '0.1.7';
const GITHUB_MAVEN_URL = 'https://maven.pkg.github.com/amply/sdk';
const GITHUB_MAVEN_CREDENTIALS_ENV = {
  username: 'AMPLY_MAVEN_USERNAME',
  password: 'AMPLY_MAVEN_PASSWORD',
};

const GITHUB_GRADLE_REPOSITORY_SNIPPET = `
        maven {
            url '${GITHUB_MAVEN_URL}'
            credentials {
                username findProperty('${GITHUB_MAVEN_CREDENTIALS_ENV.username}') ?: System.getenv('${GITHUB_MAVEN_CREDENTIALS_ENV.username}')
                password findProperty('${GITHUB_MAVEN_CREDENTIALS_ENV.password}') ?: System.getenv('${GITHUB_MAVEN_CREDENTIALS_ENV.password}')
            }
        }
`;

const GRADLE_DEPENDENCY_LINE = `    implementation("tools.amply:sdk-android:${'$'}{project.findProperty('amplySdkVersion') ?: System.getenv('AMPLY_SDK_VERSION') ?: '${DEFAULT_SDK_VERSION}'}")`;

function applyGithubMavenRepository(contents: string): string {
  if (contents.includes(GITHUB_MAVEN_URL)) {
    return contents;
  }

  const repositoriesBlock = /(repositories\s*\{[\s\S]*?\n\})/;
  if (repositoriesBlock.test(contents)) {
    return contents.replace(repositoriesBlock, match => {
      if (match.includes(GITHUB_MAVEN_URL)) {
        return match;
      }
      return match.replace(/\n\}/, `\n${GITHUB_GRADLE_REPOSITORY_SNIPPET}    }`);
    });
  }

  // Fallback: append at the end of settings block
  return `${contents}\nrepositories {${GITHUB_GRADLE_REPOSITORY_SNIPPET}}\n`;
}

function applyDependency(contents: string): string {
  if (contents.includes('tools.amply:sdk-android')) {
    return contents;
  }

  const dependenciesBlock = /(dependencies\s*\{[\s\S]*?\n\})/;
  if (dependenciesBlock.test(contents)) {
    return contents.replace(dependenciesBlock, match => {
      if (match.includes('tools.amply:sdk-android')) {
        return match;
      }
      return match.replace(/\n\}/, `\n${GRADLE_DEPENDENCY_LINE}\n}`);
    });
  }

  return `${contents}\ndependencies {\n${GRADLE_DEPENDENCY_LINE}\n}\n`;
}

type WithAmplyPluginProps = {
  sdkVersion?: string;
  /**
   * Optional escape hatch for internal builds that still rely on GitHub Packages.
   * Public installs resolve from Maven Central without any additional repositories.
   */
  useGithubFallbackRepo?: boolean;
};

function addAmplyPackageRegistration(contents: string, language: 'java' | 'kt'): string {
  // Check if AmplyPackage is already registered
  if (contents.includes('AmplyPackage')) {
    console.log('[Amply] AmplyPackage already registered in MainApplication');
    return contents;
  }

  // Add import if not present
  if (!contents.includes('import com.amply.reactnative.AmplyPackage')) {
    contents = addImports(contents, ['com.amply.reactnative.AmplyPackage'], language === 'java');
    console.log('[Amply] Added AmplyPackage import to MainApplication');
  }

  // Determine the correct syntax for the language
  const amplyPackageCall = language === 'java' ? 'add(new AmplyPackage());' : 'add(AmplyPackage())';

  // Add AmplyPackage() to the packages list in getPackages() method
  // Use mergeContents for safe, idempotent insertion
  const result = mergeContents({
    src: contents,
    newSrc: amplyPackageCall,
    tag: 'amply-package-registration',
    anchor: /(?<=\.apply\s*\{[\s]*)/,
    offset: 0,
    comment: language === 'java' ? '//' : '//',
  });

  if (result.didMerge) {
    console.log('[Amply] Registered AmplyPackage in getPackages() method');
    return result.contents;
  } else {
    console.warn('[Amply] Could not register AmplyPackage - anchor pattern not found');
    return contents;
  }
}

const withAmply: ConfigPlugin<WithAmplyPluginProps> = (config, props = {}) => {
  const {sdkVersion, useGithubFallbackRepo = false} = props;
  const version = sdkVersion ?? DEFAULT_SDK_VERSION;

  config = withGradleProperties(config, gradleConfig => {
    const exists = gradleConfig.modResults.find(
      item => item.type === 'property' && item.key === 'amplySdkVersion'
    );
    if (!exists) {
      gradleConfig.modResults.push({ type: 'property', key: 'amplySdkVersion', value: version });
    }
    return gradleConfig;
  });

  config = withProjectBuildGradle(config, gradleConfig => {
    if (useGithubFallbackRepo) {
      gradleConfig.modResults.contents = applyGithubMavenRepository(gradleConfig.modResults.contents);
    }
    gradleConfig.modResults.contents = applyDependency(gradleConfig.modResults.contents);
    return gradleConfig;
  });

  // Add Amply native module to settings.gradle
  config = withSettingsGradle(config, gradleConfig => {
    const contents = gradleConfig.modResults.contents;

    // Only add if not already present
    if (contents.includes(':amplyreactnative')) {
      console.log('[Amply] Amply module already configured in settings.gradle');
      return gradleConfig;
    }

    // Add the Amply native module include configuration after the last includeBuild statement
    const moduleConfig = "\n\ninclude ':amplyreactnative'\nproject(':amplyreactnative').projectDir = new File(rootProject.projectDir, '../../../android')";

    const lastIncludeBuildMatch = contents.lastIndexOf('includeBuild');
    let modified = contents;

    if (lastIncludeBuildMatch !== -1) {
      const nextNewlineAfterIncludeBuild = contents.indexOf('\n', lastIncludeBuildMatch);
      if (nextNewlineAfterIncludeBuild !== -1) {
        modified =
          contents.slice(0, nextNewlineAfterIncludeBuild + 1) +
          moduleConfig +
          contents.slice(nextNewlineAfterIncludeBuild + 1);
        console.log('[Amply] Added Amply module include to settings.gradle');
      }
    } else {
      // Fallback: append at the end
      modified = contents + moduleConfig;
      console.log('[Amply] Appended Amply module include to settings.gradle (no includeBuild found)');
    }

    gradleConfig.modResults.contents = modified;
    return gradleConfig;
  });

  // Add Amply native module dependency to app/build.gradle
  config = withAppBuildGradle(config, gradleConfig => {
    const contents = gradleConfig.modResults.contents;

    // Only add if not already present
    if (contents.includes(':amplyreactnative')) {
      console.log('[Amply] Amply module already configured in app/build.gradle');
      return gradleConfig;
    }

    // Add the Amply native module dependency after the first implementation statement
    const depsStart = contents.indexOf('dependencies {');
    if (depsStart !== -1) {
      const firstImplAfterDeps = contents.indexOf('implementation', depsStart);
      if (firstImplAfterDeps !== -1) {
        const lineEnd = contents.indexOf('\n', firstImplAfterDeps);
        if (lineEnd !== -1) {
          const moduleImpl = "\n\n    // Amply React Native module\n    implementation project(':amplyreactnative')";
          gradleConfig.modResults.contents =
            contents.slice(0, lineEnd) +
            moduleImpl +
            contents.slice(lineEnd);
          console.log('[Amply] Added Amply module dependency to app/build.gradle');
        }
      }
    }

    return gradleConfig;
  });

  // Register AmplyPackage in MainApplication using proper Expo API
  // This hook runs AFTER the MainApplication file is generated by Expo
  config = withMainApplication(config, async config => {
    const { contents, language } = config.modResults;
    const modified = addAmplyPackageRegistration(contents, language);
    config.modResults.contents = modified;
    return config;
  });

  return config;
};

export default withAmply;
